import { ExpressionGroupsState, ExpressionAction, Expression, ExpressionGroup } from './types';

// Generate unique IDs for groups and expressions
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Initial state with one empty group and one empty expression
export const getInitialState = (): ExpressionGroupsState => {
  const groupId = generateId();
  const expressionId = generateId();
  
  return {
    groups: {
      [groupId]: {
        expressions: {
          [expressionId]: {
            field: undefined,
            operator: undefined,
            value: [],
            isCompleted: false,
          },
        },
        operator: 'AND',
      },
    },
    operator: 'OR',
  };
};

// Helper function to check if an expression is completed
const isExpressionCompleted = (expression: Expression): boolean => {
  if (!expression.field || !expression.operator) {
    return false;
  }

  // For 'present' and 'not_present' operators, no value is needed
  if (expression.operator === 'present' || expression.operator === 'not_present') {
    return true;
  }

  // For 'between' operator, we need exactly 2 values
  if (expression.operator === 'between') {
    return expression.value.length === 2 && 
           expression.value[0] !== undefined && 
           expression.value[0] !== '' &&
           expression.value[1] !== undefined && 
           expression.value[1] !== '';
  }

  // For all other operators, we need at least one value
  return expression.value.length > 0 && 
         expression.value[0] !== undefined && 
         expression.value[0] !== '';
};

export const expressionReducer = (
  state: ExpressionGroupsState,
  action: ExpressionAction
): ExpressionGroupsState => {
  switch (action.type) {
    case 'CREATE_GROUP': {
      const { groupId } = action.payload;
      const expressionId = generateId();

      return {
        ...state,
        groups: {
          ...state.groups,
          [groupId]: {
            expressions: {
              [expressionId]: {
                field: undefined,
                operator: undefined,
                value: [],
                isCompleted: false,
              },
            },
            operator: 'AND',
          },
        },
      };
    }

    case 'REMOVE_GROUP': {
      const { groupId } = action.payload;
      const { [groupId]: removedGroup, ...remainingGroups } = state.groups;

      // Don't allow removing the last group
      if (Object.keys(remainingGroups).length === 0) {
        return state;
      }

      return {
        ...state,
        groups: remainingGroups,
      };
    }

    case 'CREATE_EXPRESSION': {
      const { groupId, expressionId } = action.payload;
      const group = state.groups[groupId];

      if (!group) return state;

      return {
        ...state,
        groups: {
          ...state.groups,
          [groupId]: {
            ...group,
            expressions: {
              ...group.expressions,
              [expressionId]: {
                field: undefined,
                operator: undefined,
                value: [],
                isCompleted: false,
              },
            },
          },
        },
      };
    }

    case 'REMOVE_EXPRESSION': {
      const { groupId, expressionId } = action.payload;
      const group = state.groups[groupId];

      if (!group) return state;

      const { [expressionId]: removedExpression, ...remainingExpressions } = group.expressions;

      // Don't allow removing the last expression in a group
      if (Object.keys(remainingExpressions).length === 0) {
        return state;
      }

      return {
        ...state,
        groups: {
          ...state.groups,
          [groupId]: {
            ...group,
            expressions: remainingExpressions,
          },
        },
      };
    }

    case 'UPDATE_EXPRESSION': {
      const { groupId, expressionId, expression: expressionUpdate } = action.payload;
      const group = state.groups[groupId];
      const currentExpression = group?.expressions[expressionId];

      if (!group || !currentExpression) return state;

      const updatedExpression: Expression = {
        ...currentExpression,
        ...expressionUpdate,
      };

      // Reset operator and value if field changes
      if (expressionUpdate.field && expressionUpdate.field.identifier !== currentExpression.field?.identifier) {
        updatedExpression.operator = undefined;
        updatedExpression.value = [];
      }

      // Reset value if operator changes
      if (expressionUpdate.operator && expressionUpdate.operator !== currentExpression.operator) {
        updatedExpression.value = [];
      }

      // Update completion status
      updatedExpression.isCompleted = isExpressionCompleted(updatedExpression);

      return {
        ...state,
        groups: {
          ...state.groups,
          [groupId]: {
            ...group,
            expressions: {
              ...group.expressions,
              [expressionId]: updatedExpression,
            },
          },
        },
      };
    }

    case 'SWITCH_GROUPS_OPERATOR': {
      return {
        ...state,
        operator: action.payload.operator,
      };
    }

    case 'SWITCH_EXPRESSIONS_OPERATOR': {
      const { groupId, operator } = action.payload;
      const group = state.groups[groupId];

      if (!group) return state;

      return {
        ...state,
        groups: {
          ...state.groups,
          [groupId]: {
            ...group,
            operator,
          },
        },
      };
    }

    case 'SET_INITIAL_STATE': {
      return action.payload.state;
    }

    case 'RESET_STATE': {
      return getInitialState();
    }

    default:
      return state;
  }
};